import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import ProjectsApi from '@api/projects.api';
import { toast } from 'react-toastify';

import Table from '@components/Table';
import MainContentHeader from '@components/MainContentHeader';
import MoreActionsButton from '@components/Table/MoreActionsButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Modal from '@components/Modal';
import Loader from '@components/Loader';

import stores from '@stores';

import * as commonS from '@styles/common';
import * as S from './styles';

import { Link } from 'react-router-dom';

export type ProjectsT = {
  _id?: string;
  number?: number;
  title?: string;
  date?: Date | string | null;
  updatedAt?: Date | string | null;
  createdAt?: Date | string | null;
};

export const headCells = [
  { title: '№', key: 'number', sortable: false, width: '3%' },
  { title: 'Name', key: 'title', sortable: false, width: '37%' },
  { title: 'Erstellung', key: 'date', sortable: false, width: '30%' },
  {
    title: 'Letzte Aktualisierung',
    key: 'updatedAt',
    sortable: false,
    width: '30%',
  },
  { title: '', key: 'actions', sortable: false },
];

const ProjectsScreen = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const { projectsStore } = stores;
  const { handleChange, isLoading, projects, setProject, project, errors } =
    projectsStore;
  const {
    layoutStore: { setConfirmModal },
  } = stores;

  useEffect(() => {
    projectsStore.getProjectsList();
  }, []);

  const handleOpenEditModal = (row: any) => {
    setIsEdit(true);
    setModalOpen(true);
    setCurrentProject(row);
  };

  const handleEdit = async () => {
    setProject({});
    await ProjectsApi.edit(currentProject).then((e: any) =>
      toast.success('The Project was edited successfully', { autoClose: 3000 })
    );
    await projectsStore.getProjectsList();
    setModalOpen(false);
    setIsEdit(false);
  };

  const handleChangeCurrentProject = ({ target }: { target: HTMLInputElement }) => {
    const { name, value } = target;
    setCurrentProject({
      ...currentProject,
      [name]: value
    });
  };

  const onDelete = (id: string) => {
    setConfirmModal({
      type: 'warning',
      open: true,
      title: 'Element wirklich löschen?',
      positiveCallback: async () => {
        await ProjectsApi.delete(id).then((e: any) =>
          toast.success('The Project was deleted successfully', { autoClose: 3000 })
        );
        await projectsStore.getProjectsList();
      },
      positiveButtonText: 'Ok',
      negativeButtonText: 'Cancel',
    });
  };

  const handleAddNew = async () => {
    setModalOpen(false);
    await ProjectsApi.add({ ...project }).then((e: any) =>
      toast.success('The Project was added successfully', { autoClose: 3000 })
    );
    await setProject({});
    await projectsStore.getProjectsList();
  };

  const handleCopy = async (id) => {
    await projectsStore.cloneProject(id);
    await projectsStore.getProjectsList();
  }

  const getFormattedRows = () =>
    projects?.map((row: ProjectsT, index: number) => ({
      id: row?._id,
      number: <strong>{index + 1}</strong>,
      title: <Link to={`/projekte/${row?._id}/standorte`}>{row?.title}</Link>,
      date: format(new Date(row?.createdAt), "dd. MMMM yyyy", { locale: de }) || '',
      updatedAt: format(new Date(row?.updatedAt), "dd. MMMM yyyy", { locale: de }) || '',
      actions:
        <MoreActionsButton
          isEdit
          isCopy
          onCopy={() => handleCopy(row?._id)}
          onEdit={() => handleOpenEditModal(row)}
          onDelete={() => onDelete(row?._id)} />,
    }));

  return (
    <commonS.MainBody>
      <MainContentHeader
        title="Projekte"
        buttonTitle="Hinzufügen"
        onButtonClick={() => setModalOpen(true)}
      />
      <Table
        headRows={headCells}
        bodyRows={getFormattedRows()}
        sorting={{
          sort: '',
          onChangeSorting: () => console.log('SortChange'),
        }}
      />

      <Modal
        modalOpen={modalOpen}
        isEdit={isEdit}
        onClose={() => {
          setModalOpen(false);
          setIsEdit(false);
        }}
        onConfirm={isEdit ? handleEdit : handleAddNew}
        setModalOpen={setModalOpen}
        title="Projekt"
      >
        <S.FormItem>
          <InputLabel htmlFor="select" shrink={false}>
            Stufe
          </InputLabel>
          <Select
            labelId="select"
            id="type"
            label="Type"
            value={isEdit ? currentProject.type : project.type}
            name="type"
            onChange={isEdit ? handleChangeCurrentProject : handleChange}
          >
            <MenuItem value="Nursery">Kindergarten</MenuItem>
            <MenuItem value="School">Unterstufe/Mittelstufe</MenuItem>
          </Select>
        </S.FormItem>
        <S.FormItem>
          <InputLabel htmlFor="title" shrink={false}>
            Name
          </InputLabel>
          <TextField
            id="title"
            value={isEdit ? currentProject.title : project.title}
            onChange={isEdit ? handleChangeCurrentProject : handleChange}
            name="title"
            variant="outlined"
          />
        </S.FormItem>
      </Modal>

      {isLoading && <Loader filled={true} />}
    </commonS.MainBody>
  );
};

export default observer(ProjectsScreen);
